import { useState, useEffect, useRef, useLayoutEffect } from "react";
import {
  Button,
  Checkbox,
  Divider,
  Input,
  Table,
  Typography,
  message,
  Spin,
} from "antd";
import {
  Link,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { CalendarOutlined, CloseCircleOutlined } from "@ant-design/icons";
import DocUpdateIcon from "../../assets/DocUpdateIcon.svg";
import HelpIcon from "../../assets/HelpIcon.svg";
import WPFooter from "../../components/WPFooter/WPFooter";
import {
  getFormData,
  getRequestStatus,
  submitSampleRequest,
  getRequestIdFromUrl,
  resolveProducts,
  FormData,
  Address,
} from "../../api/samplesIntegrationApi";
import "./SampleRequestForm.css";

interface OrderItem {
  key: string;
  totalQuantity: string;
  productGroup: string;
  genericName: string;
  productDescription: string;
  ndc: string;
}

interface GeneralInfo {
  hcpName: string;
  designation: string;
  stateLicenseNumber: string;
  phoneNumber: string;
  shippingStreet: string;
  shippingCity: string;
}

const formatPhoneNumber = (phone: string): string => {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === "1") {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
};

const SampleRequestForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [fullName, setFullName] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showTimestamp, setShowTimestamp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameMismatch, setNameMismatch] = useState(false);
  const [showOhioTooltip, setShowOhioTooltip] = useState(false);
  const [ohioArrowPx, setOhioArrowPx] = useState(0);
  const ohioIconRef = useRef<HTMLImageElement>(null);
  const ohioTooltipBoxRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requestID, setRequestID] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);

  useLayoutEffect(() => {
    if (showOhioTooltip && ohioIconRef.current && ohioTooltipBoxRef.current) {
      const iconRect = ohioIconRef.current.getBoundingClientRect();
      const tooltipRect = ohioTooltipBoxRef.current.getBoundingClientRect();
      const iconCenterX = iconRect.left + iconRect.width / 2;
      const raw = iconCenterX - tooltipRect.left - 6;
      setOhioArrowPx(Math.max(16, Math.min(tooltipRect.width - 28, raw)));
    }
  }, [showOhioTooltip]);

  useEffect(() => {
    const initializeForm = async () => {
      const requestIdFromUrl =
        getRequestIdFromUrl() || searchParams.get("requestID");

      if (!requestIdFromUrl) {
        setError("Invalid request. No request ID provided.");
        setIsLoading(false);
        return;
      }

      setRequestID(requestIdFromUrl);

      // Always re-check with the backend on every mount. The prior
      // location.state short-circuit let a browser Back into a stale
      // history entry render the form without ever asking the server if
      // the OTP was still valid; we now gate every mount on getRequestStatus.
      try {
        const status = await getRequestStatus(requestIdFromUrl);
        if (status.otpStatus !== "verified") {
          navigate(`/verify-phone?requestID=${requestIdFromUrl}`);
          return;
        }
        if (status.isClosed) {
          navigate("/form-unavailable", {
            state: {
              reason:
                status.formStatus === "submitted" ? "submitted" : "closed",
            },
          });
          return;
        }

        const response = await getFormData(requestIdFromUrl);
        setFormData(response.formData);

        // Preserve the address the user picked on AddressSelection when
        // available; otherwise default to the first address on the record.
        const preselected = location.state?.selectedAddress as Address | undefined;
        if (preselected) {
          setSelectedAddress(preselected);
        } else if (response.formData.addresses?.length > 0) {
          setSelectedAddress(response.formData.addresses[0]);
        }
      } catch (err: any) {
        console.error("Failed to fetch form data:", err);
        setError(err.message || "Failed to load form data");
        if (
          err.message?.includes("not found") ||
          err.message?.includes("expired")
        ) {
          navigate("/form-unavailable", { state: { reason: "expired" } });
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeForm();
  }, [location.state, navigate, searchParams]);

  useEffect(() => {
    if (!showTimestamp) return;
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, [showTimestamp]);

  // Compute display values from form data
  const generalInfo: GeneralInfo = {
    hcpName: formData?.hcp
      ? `${formData.hcp.firstName} ${formData.hcp.lastName}`
      : location.state?.generalInfo?.hcpName || "Loading...",
    designation:
      formData?.hcp?.designation ||
      location.state?.generalInfo?.designation ||
      "",
    stateLicenseNumber:
      selectedAddress?.stateLicense?.licNumber ||
      location.state?.generalInfo?.stateLicenseNumber ||
      "",
    phoneNumber:
      formData?.hcp?.phoneNumber ||
      location.state?.generalInfo?.phoneNumber ||
      "",
    shippingStreet: selectedAddress
      ? `${selectedAddress.street}${selectedAddress.suite ? `, ${selectedAddress.suite}` : ""}`
      : location.state?.generalInfo?.shippingStreet || "",
    shippingCity: selectedAddress
      ? `${selectedAddress.city}, ${selectedAddress.state} ${selectedAddress.zipCode}`
      : location.state?.generalInfo?.shippingCity || "",
  };

  const requestNumber =
    formData?.requestInfo?.requestNumber || location.state?.requestNumber || "";
  const requestDate =
    formData?.requestInfo?.requestDate || location.state?.requestDate || "";

  // Transform products to order items
  // Resolve from decision.drugName catalog mapping, decision.products array, or location state
  const resolvedProducts = formData?.decision
    ? resolveProducts(formData.decision)
    : location.state?.orderItems || [];

  console.log(
    "[SampleRequestForm] formData?.decision:",
    JSON.stringify(formData?.decision, null, 2),
  );
  console.log(
    "[SampleRequestForm] resolvedProducts:",
    JSON.stringify(resolvedProducts, null, 2),
  );

  const orderItems: OrderItem[] = resolvedProducts.map(
    (product: any, index: number) => ({
      key: String(index + 1),
      totalQuantity: product.totalQuantity || "",
      productGroup: product.productGroup || "",
      genericName: product.genericName || "",
      productDescription: product.productDescription || "",
      ndc: product.ndc || "",
    }),
  );

  const columns = [
    {
      title: "Total Quantity",
      dataIndex: "totalQuantity",
      key: "totalQuantity",
    },
    {
      title: "Product Group",
      dataIndex: "productGroup",
      key: "productGroup",
    },
    {
      title: "Generic Name",
      dataIndex: "genericName",
      key: "genericName",
    },
    {
      title: "Product Description",
      dataIndex: "productDescription",
      key: "productDescription",
    },
    {
      title: "NDC",
      dataIndex: "ndc",
      key: "ndc",
    },
  ];

  const expectedFullName = formData?.hcp
    ? `${formData.hcp.firstName} ${formData.hcp.lastName}`.trim()
    : "";

  const handleNameChange = (value: string) => {
    setFullName(value);
    setShowTimestamp(!!value.trim());

    if (value.trim() && expectedFullName) {
      setNameMismatch(
        value.trim().toLowerCase() !== expectedFullName.toLowerCase(),
      );
    } else {
      setNameMismatch(false);
    }
  };

  const handleCancel = () => {
    setFullName("");
    setAgreedToTerms(false);
    setShowTimestamp(false);
    navigate(-1);
  };

  const handleSubmit = async () => {
    if (!requestID) {
      message.error("Invalid request.");
      return;
    }
    if (!agreedToTerms) {
      message.error("Please agree to the terms before submitting.");
      return;
    }
    if (!fullName.trim()) {
      message.error("Please enter your full name.");
      return;
    }
    if (nameMismatch) {
      message.error("Prescriber name must match the Sample Request Form.");
      return;
    }
    if (!selectedAddress) {
      message.error("No shipping address selected.");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await submitSampleRequest(requestID, selectedAddress, {
        fullName: fullName.trim(),
        signedAt: new Date().toISOString(),
        agreedToTerms,
      });

      message.success("Request submitted successfully!");

      // Navigate to a success page or show confirmation
      navigate("/form-unavailable", {
        state: {
          reason: "success",
          submittedAt: result.submittedAt,
          requestNumber,
        },
      });
    } catch (err: any) {
      message.error(
        err.message || "Failed to submit request. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    });
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="sample-request-container">
        <header className="sample-request-header">
          <Link to="https://www.docupdate.io/">
            <img
              src={DocUpdateIcon}
              alt="DocUpdate"
              className="sample-request-logo"
            />
          </Link>
        </header>
        <main className="sample-request-main">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "50vh",
            }}
          >
            <Spin size="large" />
            <Typography.Text style={{ marginTop: 16, color: "#6b7280" }}>
              Loading form data...
            </Typography.Text>
          </div>
        </main>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="sample-request-container">
        <header className="sample-request-header">
          <Link to="https://www.docupdate.io/">
            <img
              src={DocUpdateIcon}
              alt="DocUpdate"
              className="sample-request-logo"
            />
          </Link>
        </header>
        <main className="sample-request-main">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "50vh",
            }}
          >
            <Typography.Text style={{ color: "#ef4444", fontSize: 16 }}>
              {error}
            </Typography.Text>
            <Button
              type="primary"
              onClick={() => window.location.reload()}
              style={{ marginTop: 16 }}
            >
              Try Again
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="sample-request-container">
      {/* Header */}
      <header className="sample-request-header">
        <Link to="https://www.docupdate.io/">
          <img
            src={DocUpdateIcon}
            alt="DocUpdate"
            className="sample-request-logo"
          />
        </Link>
      </header>

      {/* Main Content */}
      <main className="sample-request-main">
        <Typography.Title className="sample-request-title">
          Rx Sample Request Form
        </Typography.Title>
        <Typography.Text className="sample-request-number">
          Request #: {requestNumber || "\u2014"}
        </Typography.Text>
        <Typography.Text className="sample-request-date">
          Request Date: {requestDate || "\u2014"}
        </Typography.Text>

        {/* General Information Section */}
        <section className="sample-request-section">
          <Typography.Text className="sample-request-section-title">
            General Information
          </Typography.Text>
          <div className="sample-request-info-grid">
            <div className="sample-request-info-card">
              <Typography.Text className="info-card-label">
                Prescriber Information
              </Typography.Text>
              <Typography.Text className="info-card-value">
                {generalInfo.hcpName}
              </Typography.Text>
              {generalInfo.designation && (
                <Typography.Text className="info-card-value">
                  Professional Designation: {generalInfo.designation}
                </Typography.Text>
              )}
              <Typography.Text className="info-card-value">
                State License Number: {generalInfo.stateLicenseNumber}
              </Typography.Text>
              {generalInfo.phoneNumber && (
                <Typography.Text className="info-card-value">
                  Phone Number: {formatPhoneNumber(generalInfo.phoneNumber)}
                </Typography.Text>
              )}
            </div>
            <div className="sample-request-info-card">
              <Typography.Text className="info-card-label">
                Shipping Address
              </Typography.Text>
              <Typography.Text className="info-card-value">
                {generalInfo.shippingStreet}
              </Typography.Text>
              <Typography.Text className="info-card-value">
                {generalInfo.shippingCity}
              </Typography.Text>
            </div>
          </div>
        </section>

        {/* Sample Request Summary Section */}
        <section className="sample-request-section">
          <div className="sample-request-section-header">
            <Typography.Text className="sample-request-section-title">
              Sample Request Summary
            </Typography.Text>
          </div>
          <div
            className="ohio-hcp-notice-wrapper"
            onMouseEnter={() => {
              if (!("ontouchstart" in window)) setShowOhioTooltip(true);
            }}
            onMouseLeave={() => {
              if (!("ontouchstart" in window)) setShowOhioTooltip(false);
            }}
          >
            <div className="ohio-hcp-notice">
              <Typography.Text className="ohio-hcp-text">
                For Ohio HCPs
              </Typography.Text>
              <img
                ref={ohioIconRef}
                src={HelpIcon}
                alt="Help"
                className="ohio-hcp-icon"
                onClick={() => setShowOhioTooltip(!showOhioTooltip)}
              />
            </div>
            {showOhioTooltip && (
              <>
                {"ontouchstart" in window && (
                  <div
                    className="sr-tooltip-overlay"
                    onClick={() => setShowOhioTooltip(false)}
                  />
                )}
                <div className="sr-tooltip" ref={ohioTooltipBoxRef}>
                  <div
                    className="sr-tooltip-arrow"
                    style={{ left: `${ohioArrowPx}px` }}
                  />
                  <div className="sr-tooltip-content">
                    Prescription drug samples may only be provided to a
                    prescriber whose practice is licensed as a Terminal
                    Distributor of Dangerous Drugs (TDDD) or qualifies as exempt
                    under Ohio law. For more information on the State of Ohio
                    Board of Pharmacy Prescriber licensure requirements, how to
                    obtain a TDDD license, and reasons for exemption, please go
                    to:{" "}
                    <a
                      href="http://www.pharmacy.ohio.gov/Licensing/TDDD.aspx"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#fff", textDecoration: "underline" }}
                    >
                      http://www.pharmacy.ohio.gov/Licensing/TDDD.aspx
                    </a>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="state-disclosure-text">
            <Typography.Text className="disclosure-text">
              Prescribers and other Healthcare Providers may go to{" "}
              <Link
                to="https://www.sanofi.us/en/our-company/governance/state-disclosure"
                target="_blank"
                className="disclosure-link"
              >
                https://www.sanofi.us/en/our-company/governance/state-disclosure
              </Link>{" "}
              for State Price Disclosure Information.
            </Typography.Text>
          </div>
          <div className="sample-request-table-wrapper">
            <Table
              columns={columns}
              dataSource={orderItems}
              pagination={false}
              className="sample-request-table"
            />
          </div>
          <div className="sample-request-disclaimer">
            <Typography.Text className="disclaimer-text">
              All samples above are distributed by sanofi-aventis US and
              manufactured by sanofi-aventis U.S. LLC, Morristown NJ 07960
            </Typography.Text>
          </div>
        </section>

        {/* Electronic Signature Section */}
        <section className="sample-request-section">
          <Typography.Text className="sample-request-section-title">
            Electronic Signature & Confirmation
          </Typography.Text>

          <div className="signature-agreement">
            <Checkbox
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="signature-checkbox"
            >
              <span className="signature-agreement-text">
                I, the person identified above, certify that I am authorized by
                state law, state formulary and my collaborative agreement, if
                any, to prescribe the Rx drug samples, device units for
                demo/evaluation/experience and other items listed above. I
                further certify that the drug samples/device units are requested
                for the medical/educational needs of me, my staff and/or my
                patients and will not be sold, traded, bartered, or returned for
                credit, and no reimbursement will be sought. I understand that
                the information, which may include item value, may be retained
                by Sanofi US and submitted to federal/state government as
                required by law.
              </span>
            </Checkbox>
          </div>

          <div className="signature-inputs">
            <div className="signature-name-section">
              <Typography.Text className="signature-input-label">
                Prescriber Full Name
              </Typography.Text>
              <Input
                value={fullName}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Enter your full name"
                className="signature-name-input"
                status={nameMismatch ? "error" : undefined}
                suffix={
                  nameMismatch ? (
                    <CloseCircleOutlined style={{ color: "#ef4444" }} />
                  ) : (
                    <span />
                  )
                }
              />
              {nameMismatch && (
                <Typography.Text
                  className="signature-name-error"
                  style={{
                    color: "#ef4444",
                    fontSize: 13,
                    marginTop: 4,
                    display: "block",
                  }}
                >
                  Prescriber name must match the Sample Request Form.
                </Typography.Text>
              )}
            </div>
            <div className="signature-display-section">
              <Typography.Text className="signature-input-label">
                Electronic Signature
              </Typography.Text>
              <div className="signature-display-box">
                {fullName.trim() ? (
                  <span className="signature-text">{fullName}</span>
                ) : (
                  <span className="signature-placeholder">
                    Your signature will appear here
                  </span>
                )}
              </div>
              {showTimestamp && (
                <div className="signature-timestamp">
                  <CalendarOutlined className="timestamp-icon" />
                  <Typography.Text className="timestamp-text">
                    Request Signed on {formatDate(currentTime)}
                  </Typography.Text>
                </div>
              )}
            </div>
          </div>

          <Divider className="signature-divider" />

          <div className="signature-actions">
            <Button onClick={handleCancel} className="cancel-btn">
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={!agreedToTerms || !fullName.trim() || nameMismatch}
              className="submit-btn"
            >
              Submit Request
            </Button>
          </div>
        </section>
      </main>

      <WPFooter />
    </div>
  );
};

export default SampleRequestForm;
